import get from 'lodash/get';
import { createSelector } from 'reselect';
import { reduxKey } from './constants';
import { denormalize, schema } from '../normalizr';

type EntityStorage = {
  [schemaKey: string]: {
    [key: string]: any;
  };
};

export const getDataEntities = <TState = any>(state: TState): EntityStorage =>
  state[reduxKey] ?? {};

export const getDataEntityById = <TEntity = any, TState = any>(
  state: TState,
  entitySchema: schema.Entity,
  id: number | string
): TEntity | undefined => {
  const entities = getAllDataEntitiesForSchema<TEntity, TState>(
    state,
    entitySchema
  );
  if (entities) {
    return entities[id];
  }
};

export const getDataEntitiesByIds = <TEntity = any, TState = any>(
  state: TState,
  entitySchema: schema.Entity,
  ids: (number | string)[]
): TEntity[] => {
  const entities = getAllDataEntitiesForSchema<TEntity, TState>(
    state,
    entitySchema
  );
  if (entities) {
    return ids.map(id => entities[id]).filter(entity => !!entity);
  }
  return [];
};

export const getAllDataEntitiesForSchema = <TEntity = any, TState = any>(
  state: TState,
  entitySchema: schema.Entity
): { [k: string]: TEntity } =>
  get<any, any>(state, `${reduxKey}.${entitySchema.key}`);

export const getAllDataEntitiesForSchemaArray = <TEntity = any, TState = any>(
  state: TState,
  entitySchema: schema.Entity
): TEntity[] => {
  const entities =
    getAllDataEntitiesForSchema<TEntity, TState>(state, entitySchema) || {};
  return Object.keys(entities).map(key => entities[key]);
};

/**
 * Important! Do not use any Denormalized selectors for components
 * It will cause re-rendering, because will be not optimized for reselect
 */
export const getDenormalizedDataEntityById = <TEntity = any, TState = any>(
  state: TState,
  entitySchema: schema.Entity,
  id: number | string
): TEntity | undefined => denormalize(id, entitySchema, state[reduxKey]);

export const getAllDenormalizedDataEntitiesForSchema = <
  TEntity = any,
  TState = any
>(
  state: TState,
  entitySchema: any /* should be schema.Entity, but even though schema.Entity has isAttribute, it is not a part of the interface. Sadface. */
): { [k: string]: TEntity } =>
  denormalize(
    Object.keys(get(state, `${reduxKey}.${entitySchema.key}`, {}) as object),
    [entitySchema],
    get(state, reduxKey)
  ).reduce((result, entity) => {
    // key by idAttribute
    const idAttribute =
      typeof entitySchema.idAttribute === 'function'
        ? 'id'
        : entitySchema.idAttribute;
    result[entity[idAttribute]] = entity;
    return result;
  }, {});

export const createGetDenormalizedDataEntitiesByIds = <
  TEntity = any,
  TState = any
>() =>
  createSelector(
    (state: TState) => state,
    (_: TState, entitySchema: schema.Entity) => entitySchema,
    (_: TState, __: schema.Entity, ids: number[]) => ids,
    (state, entitySchema, ids): TEntity[] =>
      denormalize(ids, [entitySchema], state[reduxKey]) ?? []
  );
export const getDenormalizedDataEntitiesByIds =
  createGetDenormalizedDataEntitiesByIds();
